import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useProject } from '@/contexts/project-context'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2, Download, Info } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface ExitScenario {
  id: string
  scenario_name: string
  scenario_type: string | null
  exit_valuation: number
  exit_date: string | null
  assumptions: any
  waterfall_results: any
}

interface ExitWaterfallCalculatorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  scenario: ExitScenario
  onUpdate?: () => void
}

interface ShareholderDistribution {
  user_id: string
  name: string
  equity_class: string
  shares: number
  percentage: number
  liquidation_preference: number
  payout: number
  return_multiple: number
}

export default function ExitWaterfallCalculator({ 
  open, 
  onOpenChange, 
  scenario,
  onUpdate 
}: ExitWaterfallCalculatorProps) {
  const [loading, setLoading] = useState(true)
  const [distributions, setDistributions] = useState<ShareholderDistribution[]>([])
  const [totalDistributed, setTotalDistributed] = useState(0)
  const [preferredReturns, setPreferredReturns] = useState(0)
  const [commonReturns, setCommonReturns] = useState(0)
  const { currentProject } = useProject()

  useEffect(() => {
    if (open && scenario) {
      calculateWaterfall()
    }
  }, [open, scenario])

  const calculateWaterfall = async () => {
    setLoading(true)
    
    try {
      // Get cap table data from cap_table_entries
      const { data: capTableRaw } = await supabase
        .from('cap_table_entries')
        .select(`
          *,
          users (id, first_name, last_name, email),
          equity_classes (class_name)
        `)
        .eq('project_id', currentProject?.id || '')

      if (!capTableRaw) {
        throw new Error('No cap table data found')
      }

      // Transform into the format we need
      const capTableData = capTableRaw.map((entry: any) => ({
        user_id: entry.user_id || '',
        full_name: entry.users ? `${entry.users.first_name || ''} ${entry.users.last_name || ''}`.trim() || entry.users.email : 'Unknown',
        equity_class: entry.equity_classes?.class_name || 'Common',
        total_shares: entry.shares || 0,
        ownership_percentage: entry.percentage || 0,
        investment_amount: 0
      }))

      // Get equity classes with liquidation preferences
      const { data: equityClasses } = await supabase
        .from('equity_classes')
        .select('*')
        .order('seniority_rank')

      if (!equityClasses) {
        throw new Error('No equity classes found')
      }

      // Calculate waterfall distribution
      let remainingProceeds = scenario.exit_valuation
      const distributions: ShareholderDistribution[] = []
      let totalPreferred = 0
      let totalCommon = 0

      // Group shareholders by equity class
      const shareholdersByClass = capTableData.reduce((acc: any, shareholder: any) => {
        const className = shareholder.equity_class || 'Common'
        if (!acc[className]) acc[className] = []
        acc[className].push(shareholder)
        return acc
      }, {})

      // Process each equity class by seniority
      for (const equityClass of equityClasses) {
        const shareholders = shareholdersByClass[equityClass.class_name] || []
        
        if (shareholders.length === 0) continue

        // Calculate liquidation preference for this class
        const liqPrefMultiple = equityClass.liquidation_preference_multiple || 0
        if (liqPrefMultiple > 0) {
          const classLiqPref = shareholders.reduce((sum: number, sh: any) => {
            // Assuming investment amount is stored or can be calculated
            return sum + (sh.investment_amount || 0) * liqPrefMultiple
          }, 0)

          const actualPayout = Math.min(classLiqPref, remainingProceeds)
          
          // Distribute among shareholders of this class
          shareholders.forEach((sh: any) => {
            const sharePayout = (sh.investment_amount || 0) / classLiqPref * actualPayout
            distributions.push({
              user_id: sh.user_id,
              name: sh.full_name,
              equity_class: equityClass.class_name,
              shares: sh.total_shares,
              percentage: sh.ownership_percentage,
              liquidation_preference: sh.investment_amount || 0,
              payout: sharePayout,
              return_multiple: sharePayout / (sh.investment_amount || 1)
            })
            totalPreferred += sharePayout
          })

          remainingProceeds -= actualPayout
        }
      }

      // Distribute remaining proceeds pro-rata to all shareholders
      if (remainingProceeds > 0) {
        const totalShares = capTableData.reduce((sum: number, sh: any) => sum + sh.total_shares, 0)
        
        capTableData.forEach((sh: any) => {
          const proRataPayout = (sh.total_shares / totalShares) * remainingProceeds
          
          // Find if this shareholder already received liquidation preference
          const existingDist = distributions.find(d => d.user_id === sh.user_id)
          
          if (existingDist) {
            existingDist.payout += proRataPayout
            existingDist.return_multiple = existingDist.payout / (existingDist.liquidation_preference || 1)
          } else {
            distributions.push({
              user_id: sh.user_id,
              name: sh.full_name,
              equity_class: sh.equity_class || 'Common',
              shares: sh.total_shares,
              percentage: sh.ownership_percentage,
              liquidation_preference: 0,
              payout: proRataPayout,
              return_multiple: 0
            })
          }
          
          if (sh.equity_class === 'Common' || sh.equity_class === 'Options') {
            totalCommon += proRataPayout
          }
        })
      }

      // Sort by payout amount
      distributions.sort((a, b) => b.payout - a.payout)

      setDistributions(distributions)
      setTotalDistributed(distributions.reduce((sum, d) => sum + d.payout, 0))
      setPreferredReturns(totalPreferred)
      setCommonReturns(totalCommon)

      // Save waterfall results to the scenario
      await supabase
        .from('exit_scenarios')
        .update({
          waterfall_results: {
            distributions: distributions as any,
            total_distributed: totalDistributed,
            preferred_returns: totalPreferred,
            common_returns: totalCommon,
            calculation_date: new Date().toISOString()
          } as unknown as import('@/database.types').Json
        })
        .eq('id', scenario.id)

      onUpdate?.()
    } catch (error) {
      console.error('Error calculating waterfall:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`
  }

  const exportToCSV = () => {
    const headers = ['Name', 'Equity Class', 'Shares', 'Ownership %', 'Payout', 'Return Multiple']
    const rows = distributions.map(d => [
      d.name,
      d.equity_class,
      d.shares.toString(),
      formatPercentage(d.percentage),
      d.payout.toFixed(2),
      d.return_multiple.toFixed(2) + 'x'
    ])

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n')
    
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${scenario.scenario_name.replace(/\s+/g, '_')}_waterfall.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Exit Waterfall: {scenario.scenario_name}</DialogTitle>
          <DialogDescription>
            Distribution of {formatCurrency(scenario.exit_valuation)} exit proceeds
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin h-8 w-8" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Total Exit Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(scenario.exit_valuation)}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Preferred Returns</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(preferredReturns)}</div>
                  <p className="text-xs text-gray-500">
                    {formatPercentage((preferredReturns / scenario.exit_valuation) * 100)} of total
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Common Returns</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(commonReturns)}</div>
                  <p className="text-xs text-gray-500">
                    {formatPercentage((commonReturns / scenario.exit_valuation) * 100)} of total
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Distribution Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Shareholder</TableHead>
                    <TableHead>Equity Class</TableHead>
                    <TableHead className="text-right">Shares</TableHead>
                    <TableHead className="text-right">Ownership</TableHead>
                    <TableHead className="text-right">Payout</TableHead>
                    <TableHead className="text-right">Return</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {distributions.map((dist) => (
                    <TableRow key={dist.user_id}>
                      <TableCell className="font-medium">{dist.name}</TableCell>
                      <TableCell>{dist.equity_class}</TableCell>
                      <TableCell className="text-right">{dist.shares.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{formatPercentage(dist.percentage)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(dist.payout)}</TableCell>
                      <TableCell className="text-right">
                        {dist.return_multiple > 0 ? `${dist.return_multiple.toFixed(1)}x` : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-medium bg-gray-50">
                    <TableCell colSpan={4}>Total Distributed</TableCell>
                    <TableCell className="text-right">{formatCurrency(totalDistributed)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Info Note */}
            <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-md">
              <Info className="h-4 w-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Waterfall Calculation Method:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Liquidation preferences are paid first by seniority rank</li>
                  <li>Remaining proceeds are distributed pro-rata based on ownership</li>
                  <li>Return multiples are calculated based on investment amounts</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={exportToCSV} disabled={loading}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}